import { useEffect, useState } from "react";
import { DialogProps } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { validateEndAfterStart } from "../../utils/tigerTime";
import { fetchTurbineStates } from "../../api/channelSelection";
import {
  getReportRange,
  setReportRange,
  getReportTurbineState,
  setReportTurbineState,
} from "../../utils/reportDialogState";

// Port of report_date_shell_c (IDD_REPORT_DATE_SHELL): start/end date pickers
// plus — for DATE_STATE reports — a turbine state combo populated from the
// config's state list. Themed to match the Graph Channel Selection dialog.
export interface ReportDateDialogProps extends DialogProps {
  /** Turbine config whose state list fills the combo. */
  configName?: string;
  /** Show the Turbine State combo (DATE_STATE reports). */
  withState?: boolean;
  onOk?: (startDate: string, endDate: string, turbineState?: string) => void;
  onCancel?: () => void;
}

export function ReportDateDialog({
  open,
  onClose,
  onApply,
  configName = "",
  withState = false,
  onOk,
  onCancel,
}: ReportDateDialogProps) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [turbineStates, setTurbineStates] = useState<string[]>([]);
  const [turbineState, setTurbineState] = useState("");
  const [error, setError] = useState("");

  // On open: seed from the last-used range (C++ g_report_start_time /
  // g_report_end_time) and load the state list for DATE_STATE reports.
  useEffect(() => {
    if (!open) return;
    const range = getReportRange();
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setError("");
    if (!withState) return;
    let cancelled = false;
    (async () => {
      const states = configName ? await fetchTurbineStates(configName) : [];
      if (cancelled) return;
      setTurbineStates(states);
      const last = getReportTurbineState();
      setTurbineState(states.includes(last) ? last : states[0] ?? "");
    })();
    return () => {
      cancelled = true;
    };
  }, [open, configName, withState]);

  const handleOk = () => {
    if (!startDate || !endDate) {
      setError(getTigerResource("TIGER_ERROR_START_TIME"));
      return;
    }
    const rangeErr = validateEndAfterStart(startDate, endDate);
    if (rangeErr) {
      setError(rangeErr);
      return;
    }
    setError("");
    // The C++ stamps the day bounds onto the picked dates (00:00:00/23:59:59).
    setReportRange({ startDate, startTime: "00:00:00", endDate, endTime: "23:59:59" });
    if (withState) setReportTurbineState(turbineState);
    onOk?.(startDate, endDate, withState ? turbineState : undefined);
    onApply?.({ startDate, endDate, turbineState: withState ? turbineState : undefined });
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_REPORT_TIME_TITLE");

  return (
    <div style={s.overlay}>
      <div
        style={{
          ...s.dialog,
          width: 400,
          height: "auto",
          minHeight: 0,
          resize: "none",
          top: "calc(50vh - 160px)",
          left: "max(2.5vw, calc(50vw - 200px))",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={handleCancel} aria-label={getTigerResource("TIGER_RES_CLOSE")}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <div style={s.card}>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <label style={s.fieldLabel} htmlFor="report-start-date">
                  {getTigerResource("TIGER_RES_START_DATE")}
                </label>
                <LangDateInput
                  id="report-start-date"
                  style={s.input}
                  aria-label={getTigerResource("TIGER_RES_START_DATE")}
                  value={startDate}
                  onChange={setStartDate}
                />
              </div>
              <div style={s.field}>
                <label style={s.fieldLabel} htmlFor="report-end-date">
                  {getTigerResource("TIGER_RES_END_DATE")}
                </label>
                <LangDateInput
                  id="report-end-date"
                  style={s.input}
                  aria-label={getTigerResource("TIGER_RES_END_DATE")}
                  value={endDate}
                  onChange={setEndDate}
                />
              </div>
            </div>
            {withState && (
              <div style={s.field}>
                <label style={s.fieldLabel} htmlFor="report-turbine-state">
                  {getTigerResource("TIGER_RES_TURBINE_STATE")}
                </label>
                <select
                  id="report-turbine-state"
                  style={s.input}
                  aria-label={getTigerResource("TIGER_RES_TURBINE_STATE")}
                  value={turbineState}
                  onChange={(e) => setTurbineState(e.target.value)}
                >
                  {turbineStates.map((ts) => (
                    <option key={ts} value={ts}>
                      {ts}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {error && (
            <div role="alert" style={{ color: "#b00020", fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>

        <div style={s.footer}>
          <button style={s.btnPrimary} onClick={handleOk}>
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={handleCancel}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
